import axios from 'axios';

export const API_BASE_URL = `${process.env.REACT_APP_API_URL}`;

const api = axios.create({
  baseURL: API_BASE_URL,
});

export const getFullUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  getMe: () => api.get('/auth/me'),
  updateMe: (userData) => api.put('/auth/me', userData),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard'),
};

export const analyticsAPI = {
  getDashboard: (params) => api.get('/v1/analytics/dashboard', { params }),
  getTrends: (params) => api.get('/v1/analytics/trends', { params }),
  getInterventions: (params) => api.get('/v1/analytics/interventions', { params }),
};

export const documentsAPI = {
  getAll: (coachId) => api.get(`/v1/coaches/${coachId}/knowledge`),
  upload: (coachId, files) => {
    const formData = new FormData();
    // Support single file or array of files
    if (files instanceof FileList || Array.isArray(files)) {
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
    } else {
      formData.append('files', files);
    }

    return api.post(`/v1/coaches/${coachId}/knowledge/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (coachId, fileId) => api.delete(`/v1/coaches/${coachId}/knowledge/${fileId}`),
  deleteAll: (coachId) => api.delete(`/v1/coaches/${coachId}/knowledge/all`),
  getStatus: (coachId, fileId) => api.get(`/v1/coaches/${coachId}/knowledge/${fileId}/status`),
  download: (coachId, fileId) => api.get(`/v1/coaches/${coachId}/knowledge/${fileId}/download`, {
    responseType: 'blob'
  }),
};

export const settingsAPI = {
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadVideoAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/avatar-video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadVoice: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/voice', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export const referralAPI = {
  getMyReferrals: () => api.get('/v1/referrals'),
  generateCode: () => api.post('/v1/referrals/generate'),
  approveClient: (clientId) => api.put(`/v1/referrals/${clientId}/approve`),
  updateFollowUp: (clientId, followUp) => api.put(`/v1/referrals/${clientId}/followup`, { followUp }),
  removeClient: (clientId) => api.delete(`/v1/referrals/${clientId}`),
};

export const dialogsAPI = {
  getAll: (params) => api.get('/v1/dialogs', { params }),
  create: (data) => api.post('/v1/dialogs', data),
  getDialog: (dialogId) => api.get(`/v1/dialogs/${dialogId}`),
  getMessages: (dialogId) => api.get(`/v1/dialogs/${dialogId}/messages`),
};

export const summariesAPI = {
  getAll: (params) => api.get('/v1/summaries', { params }),
};

export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  getMyCoaches: () => api.get('/users/coaches'),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  create: (userData) => api.post('/auth/register', userData),
};

export const ratingsAPI = {
  rateDialog: (dialogId, rating, comment, left_with_next_step) => api.post(`/v1/ratings/dialogs/${dialogId}/rating`, { rating, comment, left_with_next_step }),
  getRatings: (params) => api.get('/v1/ratings', { params }),
  getStats: (params) => api.get('/v1/ratings/stats', { params }),
  checkLastSession: (userId) => api.get(`/v1/ratings/users/${userId}/last-session-check`),
};

export const stripeAPI = {
  // Coach endpoints
  onboardCoach: () => api.post('/stripe/coach/onboard'),
  subscribePathA: (seats) => api.post('/stripe/coach/subscribe-path-a', { seats }),
  previewPathASeatsUpdate: (seats) => api.post('/stripe/coach/subscribe-path-a/preview', { seats }),
  updatePathASeats: (seats) => api.put('/stripe/coach/subscribe-path-a', { seats }),
  approveClient: (clientId, path, price) => api.post('/stripe/coach/clients/approve', { clientId, path, price }),
  revokeClient: (clientId) => api.post('/stripe/coach/clients/revoke', { clientId }),
  changeClientPrice: (clientId, price) => api.put('/stripe/coach/clients/price', { clientId, price }),
  
  // Client endpoints
  checkoutPathB: (coachId) => api.post('/stripe/client/checkout-path-b', { coachId }),
  
  // Shared
  createPortalSession: () => api.post('/stripe/portal'),
  requestPayout: () => api.post('/stripe/coach/payout'),
  getFinancesStats: () => api.get('/stripe/coach/finances/stats'),
  syncSession: (sessionId) => api.post('/stripe/sync-session', { sessionId }),
};

export const adminAPI = {
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  getStats: () => api.get('/admin/stats'),
  processRefund: (data) => api.post('/admin/refund', data),
  overrideRevShare: (data) => api.post('/admin/coach/rev-share', data),
  changeUserPassword: (id, password) => api.post(`/admin/users/${id}/password`, { password }),
  exportCoachDialogs: (coachId) => api.get(`/v1/coaches/${coachId}/export-dialogs`, { responseType: 'blob' }),
};

export default api;
